import React, { useState, useEffect } from "react";
import { FormStyles, TextStyles } from "styles/style";
import { useAuth } from "hooks/auth";
import { api } from "supabase/database/api.ts";
import DataTable from "./DataTable";

export default () => {
  const { user } = useAuth();
  const [academicData, setAcademicData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    organization_name: '',
    major: '',
    status: '',
    period: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const columns = [
    { key: 'organization_name', header: 'Organization Name' },
    { key: 'major', header: 'Major' },
    { key: 'status', header: 'Status' },
    { key: 'period', header: 'Period' }
  ];

  useEffect(() => {
    if (user?.email) {
      loadData();
    }
  }, [user?.email]);

  const loadData = async () => {
    try {
      const data = await api.dataQueries.GetUserData({ 
        table: 'academic_info', 
        email: user.email 
      });
      setAcademicData(data);
    } catch (error) {
      console.error("Error loading academic data:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setFormData({
      organization_name: '',
      major: '',
      status: '',
      period: ''
    });
    setEditingIndex(null);
    setShowForm(true);
  };

  const handleEdit = (row, index) => {
    setFormData(row);
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIndex(null);
    setFormData({
      organization_name: '',
      major: '',
      status: '',
      period: ''
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingIndex !== null) {
        await api.dataMutations.UpdateData({ 
          table: 'academic_info',
          id: academicData[editingIndex].id, 
          data: formData 
        });
      } else {
        await api.dataMutations.CreateData({ 
          table: 'academic_info',
          data: { ...formData, user_email: user.email }
        });
      }
      
      alert("Academic information saved successfully!");
      setShowForm(false);
      loadData();
    } catch (error) {
      alert("Failed to save academic information");
      console.error("Error saving academic information:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showForm) {
    return (
      <FormStyles.FormContainer>
        <FormStyles.FormHeader>
          <FormStyles.FormTitle>
            {editingIndex !== null ? 'Edit' : 'Add'} Academic Information
          </FormStyles.FormTitle>
          <FormStyles.FormBackButton onClick={handleCancel}>
            ← Back to List
          </FormStyles.FormBackButton>
        </FormStyles.FormHeader>
        
        <FormStyles.Form onSubmit={onSubmit}>
          <FormStyles.FormRow>
            <FormStyles.FormLabel>Organization Name</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.organization_name || ''}
              onChange={(e) => handleFieldChange("organization_name", e.target.value)}
              placeholder="Enter organization name"
              required
            />
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>Major</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.major || ''}
              onChange={(e) => handleFieldChange("major", e.target.value)}
              placeholder="Enter your major"
              required
            />
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>Status</FormStyles.FormLabel>
            <TextStyles.SearchInput
              as="select"
              value={formData.status || ''}
              onChange={(e) => handleFieldChange("status", e.target.value)}
              required
            >
              <option value="">Select status</option>
              <option value="graduated">Graduated</option>
              <option value="current">Current Student</option>
              <option value="paused">On Pause</option>
              <option value="dropped">Dropped Out</option>
            </TextStyles.SearchInput>
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>Period</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.period || ''}
              onChange={(e) => handleFieldChange("period", e.target.value)}
              placeholder="e.g., 2018-2022"
              required
            />
          </FormStyles.FormRow>
          <FormStyles.FormButtonGroup>
            <FormStyles.FormSubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </FormStyles.FormSubmitButton>
            <FormStyles.FormCancelButton
              onClick={handleCancel}
            >
              Cancel
            </FormStyles.FormCancelButton>
          </FormStyles.FormButtonGroup>
        </FormStyles.Form>
      </FormStyles.FormContainer>
    );
  }

  return (
    <FormStyles.FormContainer>
      <DataTable
        data={academicData}
        columns={columns}
        onEdit={handleEdit}
        onAdd={handleAdd}
        title="Academic Information"
      />
    </FormStyles.FormContainer>
  );
};